import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, SubmitHandler, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { KTSVG, toAbsoluteUrl } from '../../../../../../_metronic/helpers';
import PreviewDropzoneWithPaste from '../../../../../modules/components/dropzone/PreviewDropzoneWithPaste';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import useDidMountEffect from '../../../../../modules/hooks/useDidMountEffect';
import { urlToFile } from '../utils';
import { schema } from './Schema';

interface Props {
  data: any;
  onClose: () => void;
  setRemarkOptionCapturesDataMap: React.Dispatch<React.SetStateAction<any>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddRemarkOptionCaptures = ({
  data,
  onClose,
  setRemarkOptionCapturesDataMap,
  isLoading,
  setIsLoading,
  setIsDirty,
}: Props) => {
  const intl = useIntl();
  const { setToast } = useToast();

  const [errorMessage, setErrorMessage] = useState('');
  const acceptedFileTypes = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
  };
  const acceptedFileSize = 10 * 1024 * 1024;

  const lastData = useRef(undefined);

  const selectedRemarkOptionKey: { optionType: string; optionValue: string } = useMemo(
    () => data?.key,
    [data]
  );

  const { addRemarkOptionCapturesSchema: validateSchema } = schema(intl);
  const defaultValues: z.infer<typeof validateSchema> = {
    captures: [
      {
        url: null,
        capture: null,
      },
    ],
  };
  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(validateSchema),
    defaultValues,
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'captures',
  });

  const currentCaptures = useWatch({ control, name: 'captures' });

  const isDirty = useMemo(() => {
    const newCapturesCount = currentCaptures.filter((item) => !item.capture_id).length;
    if (newCapturesCount > 0) return true;
    const originalCapturesCount = lastData.current.value.filter((item) => item.capture_id).length;
    const currentCapturesCount = currentCaptures.length;
    return originalCapturesCount !== currentCapturesCount;
  }, [currentCaptures]);

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty]);

  const loadFiles = useCallback(async () => {
    try {
      const dataCaptures = lastData.current.value;
      if (dataCaptures?.length > 0) {
        const captures = await Promise.all(
          dataCaptures.map(async (c, i) => {
            const newCapture = c.capture;
            const file =
              newCapture instanceof File
                ? newCapture
                : typeof newCapture === 'string'
                ? await urlToFile(newCapture)
                : null;
            return {
              url: c.url || null,
              capture_id: c.capture_id,
              capture: file,
            };
          })
        );

        reset({ captures });
      } else {
        reset(defaultValues);
      }
    } catch (err) {
      setErrorMessage(err?.message || intl.formatMessage({ id: 'MESSAGE.AN_ERROR_OCCURRED' }));
    }
  }, [reset, intl]);

  useEffect(() => {
    if (!data) return;

    lastData.current = data;
    loadFiles();
  }, [data]);

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const imagesThumbnailRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      if (imagesThumbnailRef.current) {
        const { scrollWidth, clientWidth } = imagesThumbnailRef.current;
        setIsOverflowing(scrollWidth > clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => window.removeEventListener('resize', checkOverflow);
  }, [fields]);

  // change to the tab where the error occurs
  useDidMountEffect(() => {
    const firstErrorIndex = errors?.captures?.findIndex((item) => item) ?? -1;

    if (firstErrorIndex !== -1) {
      setSelectedImageIndex(firstErrorIndex);
    }
  }, [errors]);

  const handleClose = () => {
    onClose();
  };

  const onSubmit: SubmitHandler<typeof defaultValues> = async (formData) => {
    setIsLoading(true);
    setErrorMessage('');

    const selectedRemarkOptionCaptures = formData.captures;

    try {
      setRemarkOptionCapturesDataMap((prev) => {
        const index = prev.findIndex(
          (item) =>
            item.key.optionType === selectedRemarkOptionKey.optionType &&
            item.key.optionValue === selectedRemarkOptionKey.optionValue
        );

        if (index !== -1) {
          const newState = [...prev];
          newState[index] = {
            ...newState[index],
            value: selectedRemarkOptionCaptures,
          };
          return newState;
        } else {
          return [
            ...prev,
            {
              key: selectedRemarkOptionKey,
              value: selectedRemarkOptionCaptures,
            },
          ];
        }
      });
      setIsLoading(false);
      handleClose();
    } catch (err) {
      setToast({
        show: true,
        type: 'Danger',
        header: intl.formatMessage({ id: 'MESSAGE.ERROR' }),
        message: 'Fail to add captures.',
      });
      setErrorMessage(err?.message || intl.formatMessage({ id: 'MESSAGE.AN_ERROR_OCCURRED' }));
      setIsLoading(false);
    }
  };

  return (
    <div className='d-flex flex-row-fluid flex-center bg-white rounded'>
      <form className='pt-5 w-100 w-xl-900px px-md-9' onSubmit={handleSubmit(onSubmit)} noValidate>
        {fields.map((field, index) => {
          if (index !== selectedImageIndex) {
            return null;
          }
          return (
            <div key={field.id}>
              {/* URL */}
              <div className='d-flex mb-4 align-items-center'>
                <label className='form-label w-50'>
                  URL <small>(must include http:// or https://)</small>
                </label>
                <Controller
                  name={`captures.${index}.url`}
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <input
                        {...field}
                        value={field.value ?? ''}
                        onChange={(value) => {
                          field.onChange(value.target.value || null);
                        }}
                        disabled={isLoading}
                        type='text'
                        className={clsx(
                          'form-control fw-normal fs-base',
                          fieldState.invalid && 'border border-danger'
                        )}
                        placeholder='https://example.com'
                      />
                    );
                  }}
                />
              </div>

              {/* Captures */}
              <div className='d-flex mb-4 align-items-center'>
                <label className='form-label required w-50'>Captures</label>
                <Controller
                  name={`captures.${index}.capture`}
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <PreviewDropzoneWithPaste
                        file={field.value instanceof File ? field.value : null}
                        onFileChange={(file) => {
                          if (file) {
                            field.onChange(file);
                          } else if (file === null) {
                            field.onChange(null);
                          }
                          setValue(`captures.${index}.capture_id`, null);
                        }}
                        acceptFileTypes={acceptedFileTypes}
                        maxSize={acceptedFileSize}
                        error={fieldState.error?.message}
                        containerClassName='w-100'
                        className={clsx('min-h-100px', isLoading && 'opacity-75')}
                      />
                    );
                  }}
                />
              </div>
            </div>
          );
        })}

        {/* Thumbnail/preview images */}
        <div className='overflow-auto'>
          <div
            ref={imagesThumbnailRef}
            className={clsx(
              'd-flex align-items-center gap-2 py-2',
              isOverflowing ? 'justify-content-start' : 'justify-content-center'
            )}
          >
            {currentCaptures.map((capture, index) => (
              <div
                key={index}
                className={clsx(
                  'symbol symbol-50px symbol-2by3 justify-self-center border border-2 border-primary border-active rounded-3',
                  index === selectedImageIndex && 'active'
                )}
                style={{ padding: '1px' }}
              >
                <div
                  className='btn btn-icon btn-sm btn-dark bg-dark bg-opacity-75 position-absolute start-100 translate-middle'
                  style={{ width: '15px', height: '15px' }}
                  onClick={() => {
                    const lastIndex = fields.length - 1;
                    remove(index);
                    if (selectedImageIndex > lastIndex - 1) {
                      setSelectedImageIndex(Math.max(selectedImageIndex - 1, 0));
                    }
                  }}
                >
                  <KTSVG className='svg-icon-6' path='/media/icons/duotune/arrows/arr061.svg' />
                </div>
                <div
                  className='symbol-label cursor-pointer'
                  onClick={() => setSelectedImageIndex(index)}
                  style={{
                    backgroundImage: `url(${
                      capture.capture instanceof File
                        ? URL.createObjectURL(capture.capture)
                        : toAbsoluteUrl('/media/icons/duotune/general/gen006.svg')
                    })`,
                  }}
                ></div>
              </div>
            ))}
            <div
              className='symbol symbol-50px symbol-2by3 justify-self-center border border-2 border-primary border-active rounded-3'
              style={{ padding: '1px' }}
            >
              <div
                className='symbol-label cursor-pointer'
                onClick={() => {
                  append({ url: null, capture: null });
                  setSelectedImageIndex(fields.length);
                }}
              >
                <i className='fas fa-plus'></i>
              </div>
            </div>
          </div>
        </div>

        {errorMessage && (
          <div className='text-danger my-4 border border-danger rounded p-2'>{errorMessage}</div>
        )}

        <div className='d-flex justify-content-end mt-4'>
          <button
            className='btn btn-primary d-flex align-items-center gap-2'
            type='submit'
            disabled={isLoading}
          >
            {intl.formatMessage({ id: 'COMMON.FINISH' })}
            {isLoading ? (
              <span className='spinner-border spinner-border-sm align-middle'></span>
            ) : (
              <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-3 m-0' />
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export { AddRemarkOptionCaptures };
